import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Plus, Copy, Move, Archive, ArrowUpDown } from "lucide-react";
import { TaskCard } from "./TaskCard";
import { QuickTaskForm } from "./QuickTaskForm";
import { Column, Task2 } from "@/types/tasks";

interface KanbanColumnProps {
  column: Column;
  onTaskClick: (task: Task2) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onColumnUpdate: (columns: Column[]) => void;
  onCreateTask: (taskData: Partial<Task2>) => void;
  onOpenTaskModal: (taskData: Partial<Task2>) => void;
}

export function KanbanColumn({ column, onTaskClick, onDragOver, onDrop, onColumnUpdate, onCreateTask, onOpenTaskModal }: KanbanColumnProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showQuickForm, setShowQuickForm] = useState(false);

  return (
    <div className="flex-shrink-0 w-72 relative">
      <Card className="h-full">
        <CardHeader className="pb-1">
          <CardTitle className="text-xs font-medium flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: column.color }}
              />
              {column.title}
              <Badge variant="secondary" className="ml-1 text-xs px-1 py-0 h-4">
                {column.tasks.length}
              </Badge>
            </div>
            
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-6 sm:w-6">
                  <MoreHorizontal className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setShowQuickForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Cartão
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Lista
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Move className="w-4 h-4 mr-2" />
                  Mover Lista
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Move className="w-4 h-4 mr-2" />
                  Mover Todos os Cartões
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  Ordenar por...
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive">
                  <Archive className="w-4 h-4 mr-2" />
                  Arquivar Lista
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive">
                  <Archive className="w-4 h-4 mr-2" />
                  Arquivar Todos os Cartões
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardTitle>
        </CardHeader>
        
        <CardContent 
          className="px-2 py-0.5 space-y-1 min-h-[300px]"
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          {column.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task)}
              columnColor={column.color}
            />
          ))}
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowQuickForm(true)}
            className="w-full justify-start text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/20 hover:border-muted-foreground/40 text-xs h-8"
          >
            <Plus className="w-3 h-3 mr-1" />
            Adicionar cartão
          </Button>
        </CardContent>
      </Card>

      {/* Quick Task Form Popup */}
      {showQuickForm && (
        <div className="absolute top-0 left-0 z-50">
          <QuickTaskForm
            columnId={column.id}
            columnTitle={column.title}
            onCreateTask={onCreateTask}
            onOpenFullForm={onOpenTaskModal}
            onClose={() => setShowQuickForm(false)}
          />
        </div>
      )}
    </div>
  );
}